import React, { useState } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import InputGroup from "react-bootstrap/InputGroup";
import Alert from "react-bootstrap/Alert";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faLock,
  faKey,
  faShieldHalved,
  faCircleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../contexts/AuthContext";
import { portalChangePassword } from "../../services/authService";
import { useToast } from "../../contexts/ToastContext";
import { useLoading } from "../../contexts/LoadingContext";
import { getAuthTokenKey } from "../../utils/axiosInstance";

function ChangePassword() {
  const { login, userDetail } = useAuth();
  const { showToast } = useToast();
  const { startLoading, stopLoading } = useLoading();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isTempPassword = userDetail?.mustChangePassword;

  const handleFieldChange = (field, value) => {
    if (field === "currentPassword") {
      setCurrentPassword(value);
      setValidationErrors((prev) => ({ ...prev, currentPassword: undefined }));
    }
    if (field === "newPassword") {
      setNewPassword(value);
      setValidationErrors((prev) => ({
        ...prev,
        newPassword: undefined,
        confirmPassword: undefined,
      }));
    }
    if (field === "confirmPassword") {
      setConfirmPassword(value);
      setValidationErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!currentPassword) errors.currentPassword = "Current password is required";
    if (!newPassword) {
      errors.newPassword = "New password is required";
    } else if (newPassword.length < 6) {
      errors.newPassword = "Password must be at least 6 characters";
    }
    if (!confirmPassword) {
      errors.confirmPassword = "Please confirm your new password";
    } else if (newPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }
    try {
      startLoading();
      const token = localStorage.getItem(getAuthTokenKey());
      const data = await portalChangePassword(currentPassword, newPassword, token);
      if (data.accessToken) {
        login(data.accessToken, "applicant");
      }
      showToast("Password changed successfully!", "success");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      showToast(error.message || "Failed to change password.", "error");
    } finally {
      stopLoading();
    }
  };

  const getGroupBorderClass = (errorField) =>
    validationErrors[errorField] ? "border-danger" : "";

  /* ─────────────────────────────────────────────
     Shared inline styles for password input groups
  ───────────────────────────────────────────── */
  const sharedStyles = (
    <style dangerouslySetInnerHTML={{__html: `
      .cp-input-group {
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        overflow: hidden;
        transition: all 0.2s ease-in-out;
        background-color: #fff;
      }
      .cp-input-group:focus-within {
        border-color: #275317;
        box-shadow: 0 0 0 3px rgba(39, 83, 23, 0.15);
      }
      .cp-input-group.border-danger:focus-within {
        border-color: #dc3545;
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.15);
      }
      .cp-input-group .input-group-text {
        background-color: #f8fafc;
        border: none;
        color: #64748b;
        width: 44px;
        justify-content: center;
      }
      .cp-input-group .form-control {
        border: none;
        padding: 0.7rem 0.8rem;
        font-size: 0.95rem;
      }
      .cp-input-group .form-control:focus {
        box-shadow: none;
      }
      .cp-input-group .btn-toggle {
        background-color: transparent;
        border: none;
        color: #64748b;
        padding: 0 0.85rem;
      }
      .cp-input-group .btn-toggle:hover {
        color: #275317;
      }
    `}} />
  );

  /* ─────────────────────────────────────────────
     Shared form fields (used in both views)
  ───────────────────────────────────────────── */
  const formFields = (
    <Form onSubmit={handleSubmit}>
      {/* Current Password */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold text-dark">Current Password</Form.Label>
        <InputGroup className={`cp-input-group ${getGroupBorderClass("currentPassword")}`}>
          <InputGroup.Text><FontAwesomeIcon icon={faLock} /></InputGroup.Text>
          <Form.Control
            type={showCurrent ? "text" : "password"}
            value={currentPassword}
            onChange={(e) => handleFieldChange("currentPassword", e.target.value)}
            placeholder="Enter current password"
            isInvalid={!!validationErrors.currentPassword}
          />
          <Button type="button" variant="link" className="btn-toggle text-decoration-none d-flex align-items-center" onClick={() => setShowCurrent(!showCurrent)}>
            <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} />
          </Button>
        </InputGroup>
        {validationErrors.currentPassword && (
          <Form.Text className="text-danger d-block mt-1">
            <FontAwesomeIcon icon={faCircleExclamation} className="me-1" />
            {validationErrors.currentPassword}
          </Form.Text>
        )}
      </Form.Group>

      {/* New Password */}
      <Form.Group className="mb-3">
        <Form.Label className="fw-semibold text-dark">New Password</Form.Label>
        <InputGroup className={`cp-input-group ${getGroupBorderClass("newPassword")}`}>
          <InputGroup.Text><FontAwesomeIcon icon={faKey} /></InputGroup.Text>
          <Form.Control
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => handleFieldChange("newPassword", e.target.value)}
            placeholder="Enter new password (min. 6 characters)"
            isInvalid={!!validationErrors.newPassword}
          />
          <Button type="button" variant="link" className="btn-toggle text-decoration-none d-flex align-items-center" onClick={() => setShowNew(!showNew)}>
            <FontAwesomeIcon icon={showNew ? faEyeSlash : faEye} />
          </Button>
        </InputGroup>
        {validationErrors.newPassword && (
          <Form.Text className="text-danger d-block mt-1">
            <FontAwesomeIcon icon={faCircleExclamation} className="me-1" />
            {validationErrors.newPassword}
          </Form.Text>
        )}
      </Form.Group>

      {/* Confirm Password */}
      <Form.Group className="mb-4">
        <Form.Label className="fw-semibold text-dark">Confirm New Password</Form.Label>
        <InputGroup className={`cp-input-group ${getGroupBorderClass("confirmPassword")}`}>
          <InputGroup.Text><FontAwesomeIcon icon={faShieldHalved} /></InputGroup.Text>
          <Form.Control
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => handleFieldChange("confirmPassword", e.target.value)}
            placeholder="Confirm your new password"
            isInvalid={!!validationErrors.confirmPassword}
          />
          <Button type="button" variant="link" className="btn-toggle text-decoration-none d-flex align-items-center" onClick={() => setShowConfirm(!showConfirm)}>
            <FontAwesomeIcon icon={showConfirm ? faEyeSlash : faEye} />
          </Button>
        </InputGroup>
        {validationErrors.confirmPassword && (
          <Form.Text className="text-danger d-block mt-1">
            <FontAwesomeIcon icon={faCircleExclamation} className="me-1" />
            {validationErrors.confirmPassword}
          </Form.Text>
        )}
      </Form.Group>

      <Button type="submit" className="login-button mt-2" size="xl">
        Update Password
      </Button>
    </Form>
  );

  /* ─────────────────────────────────────────────────────────────────
     VIEW 1: New user (mustChangePassword = true)
     Full-page overlay — same look as the Portal Login page
  ───────────────────────────────────────────────────────────────── */
  if (isTempPassword) {
    return (
      <section
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          zIndex: 9999,
        }}
      >
        {sharedStyles}
        <Container
          className="d-flex align-items-center justify-content-center login-background"
          fluid
          style={{ height: "100%" }}
        >
          <div className="login-modal">
            <div className="d-flex justify-content-center">
              <img src="/logo.png" alt="Logo" className="login-logo" />
            </div>
            <h5 className="text-center mb-1" style={{ color: "#275317", fontWeight: "600" }}>
              Change Your Password
            </h5>
            <p className="text-center text-muted mb-3" style={{ fontSize: "14px" }}>
              You must set a new password before accessing the portal.
            </p>
            <Alert variant="warning" className="mb-3">
              <strong>🔐 Security Notice:</strong> You are required to change
              your temporary password before accessing the portal.
            </Alert>
            {formFields}
          </div>
        </Container>
      </section>
    );
  }

  /* ─────────────────────────────────────────────────────────────────
     VIEW 2: Logged-in user changing password from sidebar
     Normal portal card inside the dashboard layout (with sidebar)
  ───────────────────────────────────────────────────────────────── */
  return (
    <div className="subMain-content">
      {sharedStyles}
      <div className="page-header mb-4">
        <h1 className="page-title">Change Password</h1>
        <p className="page-subtitle text-muted">
          Update your account security settings.
        </p>
      </div>

      <Card className="shadow-sm border-0" style={{ borderRadius: "12px", maxWidth: "550px" }}>
        <Card.Body className="p-4">
          {formFields}
        </Card.Body>
      </Card>
    </div>
  );
}

export default ChangePassword;
